import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
    inject
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { App } from '@df/core/app';
import { DfDomModule } from '@df/dom/df-dom.module';
import { StickyNativeDirective } from '@df/legacy/ui/sticky/sticky-native.directive';
import { AdvancedCommerceConfigStore } from '@df/module-advanced-commerce/store/advanced-commerce-config.store';
import { DfHeaderUiModule } from '@df/module-header-ui/df-header-ui.module';
import { ImageModule } from '@df/module-image/image.module';
import { OffClickModule } from '@df/module-off-click/off-click.module';
import { NgInject } from '@df/ng/ng-inject';
import { SearchChangeEvent } from '@df/search/search.component.def';
import { IfLoggedInDirective } from '@df/session/customer/if-logged-in.directive';
import { IfNotLoggedInDirective } from '@df/session/customer/if-not-logged-in.directive';
import { BgColorOptions, ColorOptions, LinksOptions, RatioOptions, UndefinedOption } from '@df/ui/atomic/atomic-options';
import { AfterContentInitDirective, AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ScrollService } from '@df/util/service/scroll.service';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { BehaviorSubject } from 'rxjs';
import { AccountInformationCentreNotificationBell } from '../../../account/information-centre/notification-bell/account-information-centre-notification-bell.component';
import { minibasketModalId } from '../../../basket/minibasket.component';
import { BasketQtyComponent } from '../../../basket/qty/basket-qty.component';
import { WishlistQtyComponent } from '../../../catalog/ui/wishlist/qty/wishlist-qty.component';
import { AdvancedCommerceSearchFormComponent } from '../../../module/advanced-commerce/search/form/advanced-commerce-search-form.component';
import { AdvancedCommerceSearchOutletComponent } from '../../../module/advanced-commerce/search/outlet/advanced-commerce-search-outlet.component';
import { MultisiteSwitchSimpleComponent } from '../../../multisite/switch/simple/multisite-switch-simple.component';
import { IfNotTradeStoreDirective } from '../../../multisite/trade/if-not-trade-store.directive';
import { IfTradeStoreDirective } from '../../../multisite/trade/if-trade-store.directive';
import { SearchOutletComponent } from '../../../search/ui/outlet/search-outlet.component';
import { SearchComponent } from '../../../search/ui/search.component';
import { IconsOptions } from '../../../styles/cms.config';
import { IfCheckoutProcessDirective } from '../../../ui/common/if-checkout-process.directive';
import { IfNotCheckoutProcessDirective } from '../../../ui/common/if-not-checkout-process.directive';
import { StickyFreeService } from '../../../ui/sticky-free/sticky-free.service';
import { MeganavContentComponent } from '../../meganav/meganav-content.component';
import { MeganavOverlayComponent } from '../../meganav/meganav-overlay.component';
import { MeganavComponent } from '../../meganav/meganav.component';
import { PromoBarComponent } from '../promo-bar/promo-bar.component';
import { IPageHeaderBlockContent } from './page-header.interface';
import { SeasonalProductsFields } from '../../../module/vip/seasonal-fields';
import { UserNotificationsQtyBulletDirective } from '../../../module/user-notifications/user-notifications-qty-bullet.directive';
import { DfHeaderUiHeaderDirective } from '@df/module-header-ui/directive/df-header-ui-header.directive';

export const HEADER_ID = 'header';
export const headerComponentId = 'page-header';
export const MEGANAV_GROUP_ID = 'meganav';

export const submenuColumnsField: NICmsContentField.IRepeaterField = {
    type: NICmsContentField.EFieldType.repeater,
    complex: true,
    key: 'columns',
    title: 'Columns',
    itemName: 'Column',
    itemNameField: 'title',
    defaultSize: 2,
    fields: [
        <NICmsContentField.IChoiceField>{
            key: 'variant',
            type: NICmsContentField.EFieldType.choice,
            title: 'Variant',
            options: [
                { key: 'list', title: 'List' },
                { key: 'image', title: 'Image' },
                { key: 'colors', title: 'Colours with swatches' },
                { key: 'products', title: 'Products' }
            ],
            default: 'list',
            required: true
        },
        {
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title'
        },
        <NICmsContentField.IChoiceField>{
            key: 'title_color',
            type: NICmsContentField.EFieldType.choice,
            title: 'Title color',
            options: ColorOptions,
            previewMode: 'color',
            canShow: data => data?.title
        },
        {
            key: 'space_above',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Space above',
            description: 'Add space above instead title',
            default: false,
            canShow: data => !data?.title
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            complex: true,
            key: 'list',
            title: 'List',
            itemName: 'Element',
            itemNameField: 'text',
            defaultSize: 1,
            fields: [
                <NICmsContentField.IChoiceField>{
                    key: 'list_variant',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'List variant',
                    options: [
                        { key: 'title', title: 'Title' },
                        { key: 'text', title: 'Simple text' },
                        { key: 'icon', title: 'Icon with text' },
                        { key: 'color', title: 'Colour with text' }
                    ],
                    default: 'text',
                    require: true
                },
                {
                    key: 'text',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Text'
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    translatable: false,
                    canShow: data => data?.list_variant !== 'title'
                },
                {
                    key: 'image',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Icon',
                    canShow: data => data?.list_variant === 'icon'
                },
                {
                    key: 'color',
                    type: NICmsContentField.EFieldType.colorPicker,
                    title: 'Colour',
                    previewMode: 'color',
                    canShow: data => data?.list_variant === 'color'
                },
                {
                    key: 'new_color',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Is a new colour',
                    default: false,
                    canShow: data => data?.list_variant === 'color'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'new_text_color',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'New text colour',
                    options: ColorOptions,
                    canShow: data => data?.new_color && data?.list_variant === 'color',
                    previewMode: 'color'
                }
            ],
            canShow: data => data?.variant === 'list'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            complex: true,
            key: 'color_list',
            title: 'Colour List',
            itemName: 'Element',
            itemNameField: 'text',
            defaultSize: 1,
            fields: [
                {
                    key: 'text',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Text',
                    required: true
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    translatable: false
                },
                {
                    key: 'color',
                    type: NICmsContentField.EFieldType.colorPicker,
                    title: 'Colour',
                    previewMode: 'color',
                    required: true
                },
                {
                    key: 'new_color',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Is a new colour',
                    default: false
                },
                <NICmsContentField.IChoiceField>{
                    key: 'new_text_color',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'New text colour',
                    options: ColorOptions,
                    canShow: data => data?.new_color,
                    previewMode: 'color'
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    complex: true,
                    key: 'images',
                    title: 'Colour Images',
                    itemName: 'Image',
                    itemNameField: 'image_title',
                    defaultSize: 1,
                    maxSize: 2,
                    minSize: 1,
                    fields: [
                        {
                            key: 'image',
                            type: NICmsContentField.EFieldType.image,
                            title: 'Image',
                            required: true
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'ratio',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Image Ratio',
                            options: RatioOptions,
                            required: true
                        },
                        {
                            key: 'image_link',
                            type: NICmsContentField.EFieldType.text,
                            translatable: false,
                            title: 'Image Link'
                        },
                        <NICmsContentField.INumberField>{
                            key: 'width',
                            type: NICmsContentField.EFieldType.number,
                            title: 'Image width',
                            description: 'Image width in %',
                            default: 23,
                            min: 0,
                            max: 100,
                            step: 1,
                            required: true
                        },
                        {
                            key: 'caption',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Caption'
                        },
                        {
                            key: 'image_title',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Image title'
                        }
                    ]
                }
            ],
            canShow: data => data?.variant === 'colors'
        },
        {
            key: 'image',
            type: NICmsContentField.EFieldType.image,
            title: 'Image',
            canShow: data => data?.variant === 'image'
        },
        {
            key: 'image_link',
            type: NICmsContentField.EFieldType.text,
            title: 'Image Link',
            translatable: false,
            canShow: data => data?.variant === 'image'
        },
        <NICmsContentField.IChoiceField>{
            key: 'ratio',
            type: NICmsContentField.EFieldType.choice,
            title: 'Image Ratio',
            options: RatioOptions,
            canShow: data => data?.variant === 'image'
        },
        <NICmsContentField.INumberField>{
            key: 'width',
            type: NICmsContentField.EFieldType.number,
            title: 'Image width',
            description: 'Image width in %',
            default: 23,
            min: 0,
            max: 100,
            step: 1,
            required: true,
            canShow: data => data?.variant === 'image'
        },
        {
            key: 'caption',
            type: NICmsContentField.EFieldType.text,
            title: 'Caption',
            canShow: data => data?.variant === 'image'
        },
        {
            key: 'image_title',
            type: NICmsContentField.EFieldType.text,
            title: 'Image title',
            canShow: data => data?.variant === 'image'
        },
        <NICmsContentField.IRelationField>{
            key: 'products',
            type: NICmsContentField.EFieldType.relation,
            title: 'Products',
            options: {
                title: 'Product',
                type: 'product',
                qty: 5,
                multiple: true
            },
            canShow: data => data?.variant === 'products'
        },
        {
            key: 'cta_label',
            type: NICmsContentField.EFieldType.text,
            title: 'CTA label',
            canShow: data => data?.variant !== 'products' && data?.variant !== 'colors'
        },
        {
            key: 'cta_link',
            type: NICmsContentField.EFieldType.text,
            title: 'CTA link',
            translatable: false,
            canShow: data => data?.variant !== 'products' && data?.variant !== 'colors'
        }
    ]
};

@Component({
    selector: 'page-header',
    templateUrl: 'page-header.html',
    styleUrls: ['./page-header.scss'],
    standalone: true,
    imports: [
        AccountInformationCentreNotificationBell,
        AdvancedCommerceSearchFormComponent,
        AdvancedCommerceSearchOutletComponent,
        AfterContentInitDirective,
        BasketQtyComponent,
        DfHeaderUiModule,
        DfUiModule,
        DfDomModule,
        DfHeaderUiHeaderDirective,
        ICmsClientModule,
        IfCheckoutProcessDirective,
        IfLoggedInDirective,
        IfNotCheckoutProcessDirective,
        IfNotLoggedInDirective,
        IfNotTradeStoreDirective,
        IfTradeStoreDirective,
        ImageModule,
        MeganavComponent,
        MeganavContentComponent,
        MeganavOverlayComponent,
        MultisiteSwitchSimpleComponent,
        NgClass,
        OffClickModule,
        PromoBarComponent,
        DfHeaderUiHeaderDirective,
        RouterLink,
        RouterLinkActive,
        SearchComponent,
        SearchOutletComponent,
        StickyNativeDirective,
        ToggleModule,
        TranslationModule,
        UserNotificationsQtyBulletDirective,
        WishlistQtyComponent,
        DfHeaderUiHeaderDirective
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageHeaderComponent>({
    id: headerComponentId,
    block: {
        url: 'page-header',
        autoload: 'bootstrap',
        scope: 'website'
    },
    onDemandOnly: true,
    name: 'Page Header',
    description: 'Page header component',
    fields: [
        // general
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-general',
            title: 'General'
        },
        {
            key: 'logo_image',
            type: NICmsContentField.EFieldType.image,
            title: 'Logo Image',
            default: ''
        },
        {
            key: 'logo_alt',
            type: NICmsContentField.EFieldType.text,
            title: 'Logo ALT'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            complex: true,
            key: 'menu',
            title: 'Menu Items',
            itemName: 'Item',
            itemNameField: 'text',
            defaultSize: 3,
            defaultPart: {
                components: []
            },
            fields: [
                {
                    key: 'text',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Item text',
                    default: 'Some link'
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'link_class',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Link Style',
                    options: LinksOptions,
                    default: LinksOptions[0] ? LinksOptions[0].key : undefined
                },
                <NICmsContentField.IChoiceField>{
                    key: 'color',
                    title: 'Color',
                    type: NICmsContentField.EFieldType.choice,
                    previewMode: 'color',
                    options: ColorOptions,
                    description: 'Text color'
                },
                {
                    key: 'zippy_mode',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Left submenu',

                    default: false
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    complex: true,
                    key: 'columns',
                    title: 'Columns',
                    itemName: 'Column',
                    itemNameField: 'title',
                    defaultSize: 1,
                    fields: submenuColumnsField.fields,
                    canShow: data => !data?.zippy_mode
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    complex: true,
                    key: 'submenu',
                    title: 'Submenu',
                    itemName: 'Tab',
                    itemNameField: 'title',
                    defaultSize: 1,
                    fields: [
                        {
                            key: 'title',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Title'
                        },
                        submenuColumnsField
                    ],
                    canShow: data => data?.zippy_mode
                },
                {
                    key: 'right_image',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Submenu right image',

                    default: false,
                    canShow: data => !data?.zippy_mode
                },
                {
                    key: 'center_submenu',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Center submenu content',
                    default: false,
                    canShow: data => !data?.zippy_mode && !data?.right_image
                },
                {
                    key: 'image',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Image',
                    canShow: data => data?.right_image
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    canShow: data => data?.right_image
                },
                {
                    key: 'caption',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Caption',
                    canShow: data => data?.right_image
                },
                {
                    key: 'image_title',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Image title',
                    canShow: data => data?.right_image
                }
            ]
        },
        // logo - trade
        {
            key: 'logoImageTrade',
            type: NICmsContentField.EFieldType.image,
            title: 'Logo Image - trade'
        },
        {
            key: 'logoAltTrade',
            type: NICmsContentField.EFieldType.text,
            title: 'Logo ALT - trade'
        },
        // promobar
        <NICmsContentField.IField>{
            key: 'section-promobar',
            type: NICmsContentField.EFieldType.label,
            title: 'Promo Bar'
        },
        <NICmsContentField.IChoiceField>{
            key: 'promobarItemType',
            type: NICmsContentField.EFieldType.choice,
            title: 'Item type',
            default: 'text_or_link',
            options: [
                ...UndefinedOption,
                { key: 'text_or_link', title: 'Text/Link' },
                { key: 'countdown', title: 'Countdown' },
                { key: 'expandable', title: 'Expandable' }
            ],
            canShow: (data: IPageHeaderBlockContent) => data?.promobar_turn_on
        },
        <NICmsContentField.IChoiceField>{
            key: 'specialMenuBackgroundColor',
            type: NICmsContentField.EFieldType.choice,
            title: 'Special Menu Background Color',
            options: BgColorOptions,
            previewMode: 'color',
            canShow: data => data?.promobarItemType === 'expandable'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'specialMenuItems',
            title: 'Special Menu Items',
            itemName: 'Item',
            defaultSize: 1,
            description: 'The first three items are for desktop, the rest are for mobile',
            fields: [
                <NICmsContentField.IChoiceField>{
                    key: 'backgroundColor',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Background Color',
                    options: BgColorOptions,
                    previewMode: 'color'
                },
                {
                    key: 'backgroundImage',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Background Image'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'color',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Color',
                    options: ColorOptions.filter(option => option.key === 'col-w' || option.key === 'col-b'),
                    previewMode: 'color'
                },
                {
                    key: 'topText',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Top Text',
                    default: 'Up to'
                },
                {
                    key: 'title',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Title',
                    default: 'Buy One, Get One Free',
                    required: true
                },
                {
                    key: 'bottomText',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Bottom Text',
                    default: 'Enjoy BOGO deals on selected products.'
                },
                {
                    key: 'ctaText',
                    type: NICmsContentField.EFieldType.text,
                    title: 'CTA Text',
                    default: 'Shop now'
                },
                {
                    key: 'ctaLink',
                    type: NICmsContentField.EFieldType.text,
                    title: 'CTA Link',
                    default: '/'
                }
            ],
            canShow: data => data?.promobarItemType === 'expandable'
        },
        <NICmsContentField.IField>{
            key: 'promobarEndDate',
            type: NICmsContentField.EFieldType.text,
            title: 'End date',
            description: 'Format of the date: YYYY/MM/DD',
            canShow: data => data?.promobarItemType === 'countdown'
        },
        <NICmsContentField.IField>{
            key: 'promobarEndTime',
            type: NICmsContentField.EFieldType.text,
            title: 'End time',
            description: 'Format of the time: HH:MM',
            canShow: data => data?.promobarItemType === 'countdown'
        },
        {
            key: 'promobarCountdownText',
            type: NICmsContentField.EFieldType.text,
            title: 'Text',
            canShow: data => data?.promobarItemType === 'countdown'
        },
        {
            key: 'promobarTextAfterCountdown',
            type: NICmsContentField.EFieldType.text,
            title: 'Text after countdown',
            canShow: data => data?.promobarItemType === 'countdown'
        },
        {
            key: 'promobarCtaTextAfterCountdown',
            type: NICmsContentField.EFieldType.text,
            title: 'CTA Text after countdown',
            canShow: data => data?.promobarItemType === 'countdown'
        },
        {
            key: 'promobarCtaLinkAfterCountdown',
            type: NICmsContentField.EFieldType.text,
            title: 'CTA Link after countdown',
            canShow: data => data?.promobarItemType === 'countdown'
        },
        {
            key: 'promobarCtaText',
            type: NICmsContentField.EFieldType.text,
            title: 'CTA Text',
            canShow: data => data?.promobarItemType === 'countdown'
        },
        {
            key: 'promobarCtaLink',
            type: NICmsContentField.EFieldType.text,
            title: 'CTA Link',
            canShow: data => data?.promobarItemType === 'countdown'
        },
        <NICmsContentField.IField>{
            key: 'promobar_turn_on',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Turn On',
            default: false
        },
        <NICmsContentField.IChoiceField>{
            key: 'promobar_background_color',
            type: NICmsContentField.EFieldType.choice,
            title: 'Background Color',
            options: BgColorOptions,
            previewMode: 'color',
            canShow: data => data?.promobarItemType === 'text_or_link' || data?.promobarItemType === 'countdown'
        },
        {
            key: 'promoBarStartColor',
            type: NICmsContentField.EFieldType.colorPicker,
            title: 'Start Color'
        },
        {
            key: 'promobarEndColor',
            type: NICmsContentField.EFieldType.colorPicker,
            title: 'End Color'
        },
        <NICmsContentField.IChoiceField>{
            key: 'promobar_color',
            type: NICmsContentField.EFieldType.choice,
            title: 'Color',
            options: ColorOptions,
            previewMode: 'color',
            canShow: data => data?.promobarItemType === 'text_or_link' || data?.promobarItemType === 'countdown'
        },
        {
            key: 'promobar_text',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Text',
            canShow: data => data?.promobarItemType === 'text_or_link'
        },
        {
            key: 'promobar_link',
            type: NICmsContentField.EFieldType.text,
            title: 'Link',
            canShow: data => data?.promobarItemType === 'text_or_link'
        },
        {
            key: 'promobar_closeable',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Closeable',
            description: 'Check if the promobar can be closed',
            canShow: data => data?.promobarItemType === 'text_or_link'
        },
        ...SeasonalProductsFields
    ]
})
export class PageHeaderComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'blockContent'
    })
    content!: IPageHeaderBlockContent;

    headerId = HEADER_ID;

    meganavToggleGroupId = MEGANAV_GROUP_ID;

    ifBasketPage!: boolean;

    isPromobarHidden = false;

    minibasketModalId = minibasketModalId;

    _searchTrayVisible!: boolean;

    hasContent$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    @Input()
    offCanvasState!: BehaviorSubject<boolean>;

    protected router = inject(Router);
    protected app = inject(App);
    protected renderer2 = inject(Renderer2);
    private stickyFreeService = inject(StickyFreeService);
    private afterContentInitDirectiveService = inject(AfterContentInitDirectiveService);

    protected enabledAc = !!inject(AdvancedCommerceConfigStore)?.config()?.enabled;
    private scrollBlockRelease?: () => void;
    private scrollService = inject(ScrollService);

    nodes$ = new BehaviorSubject<HTMLElement[]>([]);

    private elementRef = inject(ElementRef);

    @ViewChild('cmsNode', { read: ElementRef, static: false })
    node!: ElementRef<HTMLElement>;

    override ngOnInit() {
        super.ngOnInit();
        this.ifBasketPage = this.checkIfBasketPage();

        this.subscriptions.add(
            this.router.events.subscribe(event => {
                if (event instanceof NavigationStart) {
                    this.searchTrayVisible = false;
                }
                if (event instanceof NavigationEnd) {
                    const ifBasketPage = this.checkIfBasketPage();
                    if (ifBasketPage !== this.ifBasketPage) {
                        this.ifBasketPage = ifBasketPage;
                        this.detectChanges();
                    }
                }
            })
        );
        this.offCanvasState.subscribe(state => {
            if (state) {
                this.scrollBlockRelease = this.scrollService.blockScroll();
            } else {
                this.scrollBlockRelease?.();
            }
        });
        this.subscriptions.add(this.app.events.on('search.close').subscribe(() => (this.searchTrayVisible = false)));
        this.stickyFreeService.add({
            uid: 'page-header',
            order: 0
        });

        const initSubscription = this.afterContentInitDirectiveService.ready.subscribe(ready => {
            if (ready) {
                this.detectChanges();
            }

            this.timeoutService.setTimeout(() => {
                initSubscription.unsubscribe();
            });
        });
        this.nodes$.next([this.elementRef.nativeElement]);
        this.checkHasContent();
    }

    handleHiddenChange(value: boolean) {
        this.isPromobarHidden = value;
        this.detectChanges();
    }
    onSearchFocusChange(event: SearchChangeEvent) {
        if (event.value) {
            this.searchTrayVisible = true;
            this.detectChanges();
        }
    }
    protected closeSearchTray(event: boolean) {
        if (event && this.searchTrayVisible) {
            this.searchTrayVisible = false;
        }
    }
    get searchTrayVisible(): boolean {
        return this._searchTrayVisible;
    }
    set searchTrayVisible(value: boolean) {
        if (value !== this._searchTrayVisible) {
            this._searchTrayVisible = value;
            if (this._searchTrayVisible === false) {
                this.app.events.broadcast('close-search-tray');
            }
            if (this.client.isS || this.client.isM) {
                if (this._searchTrayVisible) {
                    this.scrollBlockRelease = this.scrollService.blockScroll();
                } else {
                    this.scrollBlockRelease?.();
                }
            }
            this.detectChanges();
        }
    }
    private checkIfBasketPage() {
        return !!this.router.url.match(/^\/basket/);
    }

    override detectChanges() {
        super.detectChanges();
        this.checkHasContent();
    }

    private checkHasContent() {
        const hasContent = this.initiated && !!this.content;
        if (this.hasContent$.value !== hasContent) {
            this.timeoutService.setTimeout(() => {
                if (hasContent) {
                    this.renderer2.addClass(this.elementRef.nativeElement, 'initiated');
                }
                this.hasContent$.next(!!hasContent);
            });
        }
    }

    openOffCanvas() {
        this.offCanvasState.next(true);
    }

    toggleCmsNode(element: HTMLElement, state: boolean) {
        const nodes = this.nodes$.value;
        if (state) {
            if (!nodes.includes(element)) {
                this.nodes$.next([...nodes, element]);
            }
        } else {
            if (nodes.includes(element)) {
                this.nodes$.next(nodes.filter(node => node !== element));
            }
        }
    }
}
